// Die Rechnung des Argus-Moduls: Texte, Zustände, Lage-Sätze, Halte der
// Leitungen, Schätzung -- rein, ohne Stores und ohne Runes (argus.test.mjs).
// Die Seiten nehmen nur, was hier steht; Zahlen auf Deutsch (Komma).

import type { Halt, HaltZustand } from "../../lib/Leitung.svelte";
import type { LageTeil } from "../../lib/lage";
import type { Laeuft } from "../../lib/laeuft";
import type { Auftrag, HailoErgebnis, HailoLive, KarteWahl, Lauf, Modell, PruefStand, RunPodStand, SammelGrund, Sammeln, SammelStatus, Schatten, SchattenFenster, Stand, VergleichSatz } from "./typen";

/** Ein Halt einer Leitung (lib/Leitung.svelte). */
export type ArgusHalt = Halt;

// ── Zahlen und Zeiten ────────────────────────────────────────────────

const zwei = (n: number) => String(n).padStart(2, "0");

/** „2026-10-28-2000" → „28.10. 20:00". */
export function laufText(lauf: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})$/.exec(lauf);
  return m ? `${m[3]}.${m[2]}. ${m[4]}:${m[5]}` : lauf;
}

/** 0.144 → „0,14 $". */
export function dollar(v: number | null | undefined, stellen = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(stellen).replace(".", ",")} $`;
}

/** 0.6365 → „64 %". */
export function prozent(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${Math.round(v * 100)} %`;
}

/** Minuten in Worten: „35 min", „4 h 12 min". */
export function minuten(min: number | null | undefined): string {
  if (min == null || !Number.isFinite(min)) return "—";
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}

/** Uhrzeit, mit Tag, wenn es nicht heute ist: „04:41", „morgen 04:41",
 *  „27.10. 18:00". */
export function uhr(ms: number | null | undefined, jetzt: Date): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const hm = `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((tag(d) - tag(jetzt)) / 86_400_000);
  if (diff === 0) return hm;
  if (diff === 1) return `morgen ${hm}`;
  if (diff === -1) return `gestern ${hm}`;
  return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}. ${hm}`;
}

/** Datum ohne Uhrzeit: „27.10.". */
export function datum(ms: number | null | undefined): string {
  if (!ms) return "—";
  const d = new Date(ms);
  return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.`;
}

/** 1043 → „1.043" (deutsche Tausender). */
export function zahl(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("de-DE");
}

// ── Klassen ──────────────────────────────────────────────────────────

/** Namen der Klassen: die der Prüfansicht (deutsch, KlassenVorgabe) und die
 *  des Trainings (wie der Detektor: person, car, …; status.json kaesten). */
export const KLASSE_TEXT: Record<string, string> = {
  person: "Person",
  auto: "Auto",
  car: "Auto",
  katze: "Katze",
  cat: "Katze",
  hund: "Hund",
  dog: "Hund",
  vogel: "Vogel",
  bird: "Vogel",
  fahrrad: "Fahrrad",
  bicycle: "Fahrrad",
  maehroboter: "Mähroboter",
  robot_lawnmower: "Mähroboter",
  rasentraktor: "Rasentraktor",
  lawn_tractor: "Rasentraktor",
};

export function klasseText(k: string): string {
  return KLASSE_TEXT[k] ?? k;
}

export interface KlassenZeile {
  klasse: string;
  text: string;
  n: number;
  /** unter dem Richtwert */
  wenig: boolean;
}

/** Geprüfte Kästen je Klasse, in der Reihenfolge der Klassen. */
export function klassenZeilen(gesamt: PruefStand, klassen: string[], richtwert: number): KlassenZeile[] {
  return klassen.map((k) => {
    const n = gesamt.je_klasse?.[k] ?? 0;
    return { klasse: k, text: klasseText(k), n, wenig: n < richtwert };
  });
}

// ── Läufe ────────────────────────────────────────────────────────────

export const PHASEN: { key: string; etikett: string }[] = [
  { key: "abgleich", etikett: "Abgleich" },
  { key: "datensatz", etikett: "Datensatz" },
  { key: "weichzeichnen", etikett: "Weichzeichnen" },
  { key: "pod_starten", etikett: "Pod" },
  { key: "hochladen", etikett: "Hochladen" },
  { key: "training", etikett: "Training" },
  { key: "hailo", etikett: "Hailo" },
  { key: "zurueckholen", etikett: "Zurück" },
];

/** Die Phasen eines Laufs als Halte. Beim Fehler steht der Fehler an der
 *  Phase, in der er kam. Akzent (fertig, aktiv) nur, solange der Lauf
 *  läuft; ein vergangener Lauf ist ruhig („da", docs/gestaltung.md
 *  Leitung). */
export function phasenHalte(l: Lauf): ArgusHalt[] {
  const vorbei = l.phase === "fertig";
  const erledigt: HaltZustand = l.aktiv ? "fertig" : "da";
  const idx = PHASEN.findIndex((p) => p.key === l.phase);
  // Bei fehler/abgebrochen zeigt status.json die Endphase; die letzte echte
  // Phase steht dann nicht mehr drin -- die Meldung sagt, wo es war.
  const endeBei = l.phase === "fehler" || l.phase === "abgebrochen" ? letztePhase(l) : -1;
  return PHASEN.map((p, i) => {
    let zustand: HaltZustand = "wartet";
    if (vorbei) zustand = "da";
    else if (idx >= 0) zustand = i < idx ? erledigt : i === idx ? (l.aktiv ? "aktiv" : l.haengt ? "fehler" : "wartet") : "wartet";
    else if (endeBei >= 0) zustand = i < endeBei ? "da" : i === endeBei ? (l.phase === "fehler" ? "fehler" : "ausgesetzt") : "wartet";
    return { key: p.key, etikett: p.etikett, ort: "", wann: zustand === "aktiv" ? "jetzt" : zustand === "fehler" ? (l.haengt ? "stumm" : "Fehler") : "", zustand };
  });
}

/** Wo ein beendeter Lauf stehen blieb: aus dem, was er schon hatte. */
function letztePhase(l: Lauf): number {
  const i = (k: string) => PHASEN.findIndex((p) => p.key === k);
  if (l.hailo && l.hailo.status !== "folgt") return i("zurueckholen");
  if ((l.metriken?.length ?? 0) > 0 || l.epoche > 0) return i("training");
  if (l.gpu) return i("pod_starten");
  if (l.weichgezeichnet) return i("pod_starten");
  if (l.bilder?.train) return i("weichzeichnen");
  return i("abgleich");
}

export type Ton = "ok" | "warn" | "danger" | "accent" | "leise" | "neutral";

/** Zustand eines Laufs fürs StatusBadge. */
export function laufZustand(l: Lauf): { text: string; ton: Ton } {
  if (l.aktiv) return { text: "läuft", ton: "accent" };
  if (l.haengt) return { text: "meldet sich nicht", ton: "warn" };
  switch (l.phase) {
    case "fertig":
      return { text: "fertig", ton: "ok" };
    case "fehler":
      return { text: "Fehler", ton: "danger" };
    case "abgebrochen":
      return { text: "abgebrochen", ton: "leise" };
    default:
      return { text: l.phase, ton: "leise" };
  }
}

/** Ein Probelauf: wenige Epochen (zum Ausprobieren der Kette). */
export function istProbe(l: Lauf): boolean {
  return l.epochen > 0 && l.epochen <= 5;
}

/** Bestes mAP50 der Metriken: Wert und Epoche. */
export function bestes(l: Lauf): { map50: number; epoche: number } | null {
  let b: { map50: number; epoche: number } | null = null;
  for (const m of l.metriken ?? []) if (!b || m.map50 > b.map50) b = { map50: m.map50, epoche: m.epoche };
  return b;
}

/** Kosten eines Laufs: tatsächlich, sonst bisher. */
export function laufKosten(l: Lauf): number | undefined {
  return l.kosten?.tatsaechlich ?? l.kosten_bisher;
}

/** Dauer eines Laufs in Minuten (Pod-Lebensdauer, sonst Start bis Ende). */
export function laufDauer(l: Lauf, jetzt: Date): number | undefined {
  if (l.dauer_min?.tatsaechlich != null) return l.dauer_min.tatsaechlich;
  if (l.dauer_s) return l.dauer_s / 60;
  if (!l.start) return undefined;
  const bis = l.beendet || (l.aktiv ? jetzt.getTime() : l.aktualisiert);
  return bis ? (bis - l.start) / 60_000 : undefined;
}

/** Fortschritt eines laufenden Trainings in Prozent (Epoche + Schritt). */
export function trainingProzent(l: Lauf): number | undefined {
  const f = l.fortschritt;
  if (f && f.epochen > 0) {
    const inEpoche = f.schritte > 0 && f.teil === "lernen" ? f.schritt / f.schritte : f.teil === "pruefen" ? 1 : 0;
    return Math.min(100, Math.round(((f.epoche - 1 + inEpoche) / f.epochen) * 100));
  }
  if (l.epochen > 0 && l.phase === "training") return Math.round((l.epoche / l.epochen) * 100);
  return undefined;
}

/** Was ein laufender Lauf gerade tut, in Worten. */
export function laufWas(l: Lauf): string {
  switch (l.phase) {
    case "abgleich":
      return "gleicht ab";
    case "datensatz":
      return "stellt Daten zusammen";
    case "weichzeichnen":
      return "zeichnet weich";
    case "pod_starten":
      return "startet den Pod";
    case "hochladen":
      return "lädt hoch";
    case "training":
      return "trainiert";
    case "hailo":
      return "wandelt für den Hailo";
    case "zurueckholen":
      return "holt zurück";
    default:
      return l.phase;
  }
}

/** „Läuft gerade" (Statusleiste, Baum, Telefon): der laufende Lauf, sonst
 *  eine laufende Umwandlung für den Hailo. */
export function argusLaeuft(s: Stand | undefined): Laeuft | undefined {
  const l = s?.laeufe.find((x) => x.aktiv);
  if (!l) {
    const h = s?.laeufe.find((x) => x.hailo_live?.aktiv);
    if (!h?.hailo_live) return undefined;
    const was = hailoWas(h.hailo_live);
    return {
      id: "argus-hailo",
      name: "Argus",
      was,
      pfad: "/argus/modelle/hailo",
      ton: "laeuft",
      titel: `Argus ${was} (Lauf ${laufText(h.lauf)})`,
    };
  }
  const was = laufWas(l);
  const p = l.phase === "training" ? trainingProzent(l) : undefined;
  const titel = `Argus ${was}${l.phase === "training" && l.epochen ? ` · Epoche ${l.fortschritt?.epoche ?? l.epoche} von ${l.epochen}` : ""} (Lauf ${laufText(l.lauf)})`;
  return {
    id: "argus",
    name: "Argus",
    was,
    fortschritt: p,
    bis: l.eta ? new Date(l.eta).toISOString() : undefined,
    pfad: "/argus/training",
    ton: "laeuft",
    titel,
  };
}

// ── Neuer Lauf ───────────────────────────────────────────────────────

/** Schätzung aus runpod.json: Dauer = fest + Epochen × je Epoche,
 *  Kosten = Preis × Dauer. */
export function schaetzung(r: RunPodStand | undefined, epochen: number): { min: number; kosten: number } | null {
  if (!r || r.fest_min == null || r.je_epoche_min == null || !r.preis_h) return null;
  const min = r.fest_min + epochen * r.je_epoche_min;
  return { min, kosten: (r.preis_h * min) / 60 };
}

// ── Kartenwahl: nach Gesamtkosten, nicht nach Stundenpreis ───────────
// Tim 29.10.2026: „Die günstigste und die ausgewählte passen hier noch
// nicht zusammen." Argus nimmt die Karte mit den geringsten geschätzten
// Kosten (Preis × Dauer): eine schnellere zu 0,28 $/h kann billiger sein
// als eine langsame zu 0,24 $/h.

/** „NVIDIA RTX 2000 Ada Generation" → „RTX 2000 Ada". */
export function karteName(id: string | null | undefined): string {
  return (id ?? "").replace(/^NVIDIA /, "").replace(/ Generation$/, "");
}

/** „0,24 $/h × ~98 min" -- woraus sich die Kosten einer Karte ergeben. */
export function preisMalDauer(preisH: number | null | undefined, min: number | null | undefined): string {
  if (!preisH || min == null || !Number.isFinite(min)) return "";
  return `${dollar(preisH)}/h × ~${Math.round(min)} min`;
}

/** Womit die Karte eines Laufs beim Start verglichen wurde (status.json
 *  wahl): die teureren als „vor …", günstigere davor waren nicht frei
 *  (genommen wird die erste freie). Ohne wahl: null. */
export function wahlVergleich(l: Pick<Lauf, "gpu" | "wahl">): { vor: string; nichtFrei: string } | null {
  const w = l.wahl ?? [];
  if (!w.length || !l.gpu) return null;
  const i = w.findIndex((k) => k.id === l.gpu);
  const text = (ks: KarteWahl[]) => ks.map((k) => `${karteName(k.id)} ≈ ${dollar(k.kosten)}`).join(", ");
  const danach = i < 0 ? w : w.slice(i + 1);
  const davor = i < 0 ? [] : w.slice(0, i);
  return { vor: danach.length ? `vor ${text(danach)}` : "", nichtFrei: davor.length ? text(davor) : "" };
}

/** Wann ein „nacht"-Auftrag startet: um 04:00 (ablauf.py, NACHT). */
export function planText(a: Auftrag | undefined, jetzt: Date, nacht = "04:00"): string {
  if (!a || a.wann === "absagen") return "";
  if (a.wann === "jetzt") return "startet jetzt (binnen einer Minute)";
  const stunde = Number(nacht.split(":")[0]) || 4;
  const h = jetzt.getHours();
  if (h < stunde) return `heute um ${nacht}`;
  if (h === stunde) return "startet jetzt (Nachtlauf)";
  return `heute Nacht ${nacht}`;
}

// ── Übersicht ───────────────────────────────────────────────────────

/** Der beste fertige Lauf (mAP50). */
export function bestesModell(s: Stand): Lauf | undefined {
  let b: Lauf | undefined;
  for (const l of s.laeufe) {
    if (l.phase !== "fertig" || l.map50 == null) continue;
    if (!b || (l.map50 ?? 0) > (b.map50 ?? 0)) b = l;
  }
  return b;
}

/** Die Kette vom Bild zum eigenen Modell, mit echtem Stand. */
export function ketteHalte(s: Stand, jetzt: Date): ArgusHalt[] {
  const g = s.gesamt;
  const aktiv = s.laeufe.find((l) => l.aktiv);
  const fertig = s.laeufe.find((l) => l.phase === "fertig");
  const hef = s.modelle.find((m) => m.hef && (m.ergebnis?.passt ?? m.hailo?.passt)) ?? s.modelle.find((m) => m.hef);
  const hailoLaeuft = s.laeufe.find((l) => l.hailo_live?.aktiv);
  const sammeln = sammelnHalt(s, g);
  const pruefen: ArgusHalt = {
    key: "pruefen",
    etikett: "Prüfen",
    ort: g.offen ? `${zahl(g.offen)} offen` : g.geprueft ? "alles geprüft" : "nichts zu prüfen",
    wann: `${zahl(g.geprueft)} geprüft`,
    zustand: g.geprueft ? "da" : "wartet",
    titel: `im Training ${zahl(g.training ?? 0)} (davon ${zahl(g.training_leer ?? 0)} ohne Objekt), im Prüfsatz ${zahl(g.pruefsatz ?? 0)}; nicht im Training: ${zahl(g.verworfen - (g.aussortiert ?? 0))} unbrauchbar, ${zahl(g.aussortiert ?? 0)} aussortiert`,
  };
  let trainieren: ArgusHalt;
  if (aktiv) {
    const f = aktiv.fortschritt;
    trainieren = {
      key: "training",
      etikett: "Trainieren",
      ort: aktiv.phase === "training" ? `Epoche ${f?.epoche ?? aktiv.epoche} von ${aktiv.epochen}` : laufWas(aktiv),
      wann: aktiv.eta ? `fertig gegen ${uhr(aktiv.eta, jetzt)}` : "RunPod",
      zustand: "aktiv",
    };
  } else if (s.auftrag && s.auftrag.wann !== "absagen") {
    trainieren = { key: "training", etikett: "Trainieren", ort: `geplant · ${s.auftrag.epochen} Epochen`, wann: planText(s.auftrag, jetzt, s.runpod?.nacht), zustand: "wartet" };
  } else if (fertig) {
    trainieren = { key: "training", etikett: "Trainieren", ort: `mAP50 ${prozent(fertig.map50)}`, wann: `Lauf ${laufText(fertig.lauf)}`, zustand: "da" };
  } else {
    const letzter = s.laeufe[0];
    trainieren = {
      key: "training",
      etikett: "Trainieren",
      ort: letzter ? laufZustand(letzter).text : "noch kein Lauf",
      wann: letzter ? `Lauf ${laufText(letzter.lauf)}` : "RunPod",
      zustand: letzter?.phase === "fehler" ? "fehler" : "wartet",
    };
  }
  const passt = hef?.ergebnis?.passt ?? hef?.hailo?.passt;
  const hailo: ArgusHalt = hailoLaeuft?.hailo_live
    ? { key: "hailo", etikett: "Für den Hailo", ort: hailoWas(hailoLaeuft.hailo_live), wann: `Lauf ${laufText(hailoLaeuft.lauf)}`, zustand: "aktiv" }
    : hef
      ? { key: "hailo", etikett: "Für den Hailo", ort: passt ? "HEF passt" : passt === false ? "HEF passt nicht" : "HEF da", wann: `Lauf ${laufText(hef.lauf)}`, zustand: "da", titel: hef.ergebnis ? `mAP50 ONNX ${prozent(hef.ergebnis.map50_onnx)}, HEF ${prozent(hef.ergebnis.map50_hef)}${hef.ergebnis.stufe != null ? `, Stufe ${hef.ergebnis.stufe}` : ""}${hef.ergebnis.rechner ? ` auf ${hef.ergebnis.rechner}` : ""}` : undefined }
      : { key: "hailo", etikett: "Für den Hailo", ort: "noch kein HEF", wann: "nach dem Training", zustand: aktiv?.phase === "hailo" ? "aktiv" : "wartet", titel: "Der Pod wandelt nach dem Training um, sonst argus-hailo auf forge (nachts 04–08 Uhr oder mit Auftrag)" };
  const schatten = schattenHalt(s.schatten, !!hef);
  return [sammeln, pruefen, trainieren, hailo, schatten];
}

// ── Sammeln ──────────────────────────────────────────────────────────

/** Die Gründe der Auswahl, in Rang-Reihenfolge, mit Kurzwort. */
export const SAMMEL_GRUENDE: { key: SammelGrund; text: string; titel: string }[] = [
  { key: "markiert", text: "markiert", titel: "deine Markierung im Alltag (Fehlalarm, etwas fehlt)" },
  { key: "abweichung", text: "uneinig", titel: "eigenes Modell und Lehrer sehen Verschiedenes" },
  { key: "unsicher", text: "unsicher", titel: "eigenes Modell zwischen 0,3 und 0,6" },
  { key: "selten", text: "selten", titel: "Katze, Hund, Vogel, Fahrrad, Rasentraktor" },
  { key: "stichprobe", text: "Zufall", titel: "Stichprobe der sicheren" },
  { key: "leer", text: "leer", titel: "ohne Objekt, übernimmt der Lehrer (Hintergrund)" },
];

/** Label von Argus/des Modells (englisch) → eigene Klasse als Text. */
const LABEL_KLASSE: Record<string, string> = {
  person: "person", car: "auto", cat: "katze", dog: "hund", bird: "vogel", bicycle: "fahrrad",
  robot_lawnmower: "maehroboter", lawn_tractor: "rasentraktor",
};
export function labelText(l: string): string {
  return klasseText(LABEL_KLASSE[l] ?? l);
}

/** „2026-10-29-nacht" → „Mi 29.10." (Aufnahmetag); sonst die Kennung. */
export function standName(stand: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})-nacht$/.exec(stand);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return `${["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()]} ${m[3]}.${m[2]}.`;
  }
  const sch = /^(\d{4})-(\d{2})-(\d{2})-schatten$/.exec(stand);
  if (sch) return `Schatten ${sch[3]}.${sch[2]}.`;
  const mk = /^(\d{4})-(\d{2})-(\d{2})-markiert$/.exec(stand);
  if (mk) return `Markiert ${mk[3]}.${mk[2]}.`;
  if (stand === "2026-10-26-erste") return "Erste Probe 25.–26.10.";
  if (stand === "2026-10-27-dicht") return "Dichte Probe 25.–27.10.";
  return stand;
}

/** Wie weit ein Sammellauf ist (0..1), null ohne Plan. */
export function sammelnAnteil(st: SammelStatus | undefined): number | null {
  if (!st || !st.geplant) return null;
  if (st.phase === "fertig") return 1;
  return Math.min(1, st.proben / st.geplant);
}

/** Zustand eines Sammellaufs als kurzes Wort mit Ton. */
export function sammelnZustand(st: SammelStatus | undefined): { text: string; ton: "ok" | "warn" | "danger" | "accent" | "leise" } {
  if (!st) return { text: "noch nie", ton: "leise" };
  switch (st.phase) {
    case "fertig":
      return { text: `${zahl(st.behalten ?? 0)} Bilder`, ton: "ok" };
    case "fehler":
      return { text: "Fehler", ton: "danger" };
    case "aus":
      return { text: "aus", ton: "leise" };
    case "uebersprungen":
      return { text: "schon da", ton: "leise" };
    case "wartet":
      return { text: st.aktiv ? "wartet auf die Karte" : "stumm", ton: st.aktiv ? "accent" : "warn" };
    case "auswahl":
    case "ablegen":
      return { text: st.aktiv ? "wählt aus" : "stumm", ton: st.aktiv ? "accent" : "warn" };
    default:
      return st.aktiv ? { text: `${Math.round((sammelnAnteil(st) ?? 0) * 100)} %`, ton: "accent" } : { text: "stumm", ton: "warn" };
  }
}

function sammelnHalt(s: Stand, g: PruefStand): ArgusHalt {
  const sa: Sammeln | undefined = s.sammeln;
  const st = sa?.status;
  if (st?.aktiv) {
    return { key: "sammeln", etikett: "Sammeln", ort: sammelnZustand(st).text, wann: standName(st.stand), zustand: "aktiv" };
  }
  if (st?.phase === "fehler") {
    return { key: "sammeln", etikett: "Sammeln", ort: "Fehler", wann: standName(st.stand), zustand: "fehler", titel: st.fehler };
  }
  return {
    key: "sammeln",
    etikett: "Sammeln",
    ort: g.bilder ? `${zahl(g.bilder)} Bilder` : "noch keine Bilder",
    wann: sa?.naechster ? `nächster ${sa.um}` : g.zuletzt ? `zuletzt ${datum(g.zuletzt)}` : s.einstellungen.herkunft,
    zustand: g.bilder ? "da" : "wartet",
    titel: `jede Nacht ${sa?.um ?? "01:00"} auf ${s.einstellungen.herkunft}, das eigene Modell wählt aus; Abgleich ${s.einstellungen.abgleich}`,
  };
}

/** Lage-Satz der Übersicht. */
export function uebersichtLage(s: Stand, jetzt: Date = new Date()): LageTeil[] {
  const t: LageTeil[] = [];
  const aktiv = s.laeufe.find((l) => l.aktiv);
  const haengt = s.laeufe.find((l) => l.haengt);
  if (!s.bereit) return [{ text: s.grund ?? "Ablage fehlt", ton: "danger" }];
  if (haengt) t.push({ text: `Lauf ${laufText(haengt.lauf)} meldet sich nicht`, ton: "warn" });
  if (aktiv) t.push({ text: `Training ${laufWas(aktiv)}`, ton: "accent" });
  if (s.gesamt.offen) t.push({ text: `${zahl(s.gesamt.offen)} Bilder offen` });
  else if (s.gesamt.geprueft) t.push({ text: "alles geprüft", ton: "ok" });
  if (!aktiv && s.auftrag && s.auftrag.wann !== "absagen") t.push({ text: `Training ${planText(s.auftrag, jetzt, s.runpod?.nacht)}` });
  return t;
}

/** Lage-Satz der Läufe-Tabelle. */
export function laeufeLage(s: Stand): LageTeil[] {
  const t: LageTeil[] = [];
  const aktiv = s.laeufe.find((l) => l.aktiv);
  if (aktiv) t.push({ text: `${laufText(aktiv.lauf)} läuft`, ton: "accent" });
  const letzter = s.laeufe.find((l) => !l.aktiv);
  if (letzter?.phase === "fehler") t.push({ text: `letzter Lauf gescheitert (${laufText(letzter.lauf)})`, ton: "danger" });
  else if (letzter?.haengt) t.push({ text: `${laufText(letzter.lauf)} meldet sich nicht`, ton: "warn" });
  if (s.laeufe_monat) t.push({ text: `diesen Monat ${s.laeufe_monat} auf RunPod · ${dollar(s.kosten_monat)}` });
  if (!t.length) t.push({ text: s.laeufe.length ? "nichts läuft" : "noch kein Lauf" });
  return t;
}

/** Was im Baum rechts an „Bilder prüfen" bzw. einem Stand steht. */
export function offenText(n: number): string {
  return n ? `${zahl(n)} offen` : "";
}

// ── Prüfen ──────────────────────────────────────────────────────────

/** Namen der Kameras (Argus /etc/argus/argus.yaml); unbekannte heißen wie
 *  ihr Kürzel. */
export const KAMERA_NAME: Record<string, string> = {
  backyard: "Garten",
  entrance: "Einfahrt",
  yard: "Vorgarten",
  entry: "Eingang",
};

export function kameraName(k: string): string {
  return KAMERA_NAME[k] ?? k;
}

/** „Heute 14:12", „Gestern 14:12", „Mo 27.10. 14:12" (mit Sekunden auf
 *  Wunsch) -- wie Argus' zeitpunkt(). */
export function zeitpunkt(ms: number, mitSekunden = false, jetzt = new Date()): string {
  const d = new Date(ms);
  const u = `${zwei(d.getHours())}:${zwei(d.getMinutes())}${mitSekunden ? `:${zwei(d.getSeconds())}` : ""}`;
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((tag(jetzt) - tag(d)) / 86_400_000);
  if (diff === 0) return `Heute ${u}`;
  if (diff === 1) return `Gestern ${u}`;
  const wt = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()];
  return `${wt} ${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}. ${u}`;
}

/** Farbe je Klasse im Bild (wie Argus). Kastenfarben sind die Farben der
 *  Objekte im Kamerabild, keine Zustandsfarben der Oberfläche. */
export const KLASSE_FARBE: Record<string, string> = {
  person: "#3b82f6",
  auto: "#f97316",
  katze: "#22c55e",
  hund: "#14b8a6",
  vogel: "#eab308",
  fahrrad: "#a855f7",
  maehroboter: "#ec4899",
  rasentraktor: "#84cc16",
};

/** Überlappung zweier Kästen (IoU). */
export function ueberlappung(a: readonly number[], b: readonly number[]): number {
  const w = Math.min(a[2], b[2]) - Math.max(a[0], b[0]);
  const h = Math.min(a[3], b[3]) - Math.max(a[1], b[1]);
  if (w <= 0 || h <= 0) return 0;
  const s = w * h;
  return s / ((a[2] - a[0]) * (a[3] - a[1]) + (b[2] - b[0]) * (b[3] - b[1]) - s);
}

// ── Hailo (Schritt 4) ───────────────────────────────────────────────

export const HAILO_PHASEN: { key: string; etikett: string }[] = [
  { key: "wartet", etikett: "Wartet" },
  { key: "parsen", etikett: "Parsen" },
  { key: "optimieren", etikett: "Optimieren" },
  { key: "kompilieren", etikett: "Kompilieren" },
  { key: "pruefen", etikett: "Prüfen" },
];

/** Wo optimiert wird (hailo.sh, RECHNER): Text und grobe Dauer. */
export const HAILO_RECHNER: Record<string, { text: string; dauer: string }> = {
  homeserver2: { text: "homeserver2 · GTX 1050 Ti, Stufe 2", dauer: "≈ 55 min" },
  forge: { text: "forge · CPU, Stufe 1", dauer: "≈ 13 min" },
};

export function hailoWas(h: HailoLive): string {
  switch (h.phase) {
    case "wartet":
      return "wartet auf die Karte";
    case "parsen":
      return "parst für den Hailo";
    case "optimieren":
      return "optimiert für den Hailo";
    case "kompilieren":
      return "kompiliert für den Hailo";
    case "pruefen":
      return "prüft das HEF";
    default:
      return h.phase;
  }
}

/** Rechner der Umwandlung kurz: „runpod: NVIDIA GeForce RTX 3090" →
 *  „RTX 3090 (RunPod)", sonst der Hostname. */
export function rechnerKurz(r: string | null | undefined): string {
  const m = /^runpod:\s*(.*)$/i.exec(r ?? "");
  if (!m) return r ?? "";
  const karte = karteName(m[1]).replace(/^GeForce /, "");
  return karte ? `${karte} (RunPod)` : "RunPod";
}

/** Der eine Rechner, auf dem alle bisherigen Phasen liefen -- er steht
 *  einmal über der Unterleitung statt unter jedem Halt. Liefen sie auf
 *  verschiedenen, "" (dann nennt ihn jeder Halt). */
export function hailoRechner(h: HailoLive): string {
  const alle = new Set(Object.values(h.phasen ?? {}).map((d) => d.rechner).filter(Boolean));
  return alle.size === 1 ? rechnerKurz([...alle][0]) : "";
}

/** Dauer einer Phase: kurze in Sekunden, sonst Minuten. */
function phasenDauer(s: number): string {
  return s < 60 ? `${Math.round(s)} s` : minuten(s / 60);
}

/** Die Phasen der Umwandlung als Halte („wartet" nur, wenn es vorkam). */
export function hailoHalte(h: HailoLive): ArgusHalt[] {
  const phasen = HAILO_PHASEN.filter((p) => p.key !== "wartet" || h.phase === "wartet" || !!h.phasen?.wartet);
  const idx = phasen.findIndex((p) => p.key === h.phase);
  // Mehrere Rechner: jeder Halt nennt seinen, leere Zeile als Platzhalter,
  // damit die Dauer in allen Spalten auf derselben Höhe steht.
  const gemischt = Object.keys(h.phasen ?? {}).length > 0 && !hailoRechner(h);
  return phasen.map((p, i) => {
    let zustand: HaltZustand = "wartet";
    const d = h.phasen?.[p.key];
    if (h.phase === "fertig") zustand = "da";
    else if (h.phase === "fehler") zustand = d ? "da" : "wartet";
    else if (idx >= 0) zustand = i < idx ? (h.aktiv ? "fertig" : "da") : i === idx ? (h.aktiv ? "aktiv" : "fehler") : "wartet";
    const wann = d ? phasenDauer(d.dauer_s) : zustand === "aktiv" ? "jetzt" : "";
    const ort = gemischt ? rechnerKurz(d?.rechner) || "\u00a0" : "";
    return { key: p.key, etikett: p.etikett, ort, wann, zustand };
  });
}

/** Ergebnis einer Umwandlung fürs StatusBadge. */
export function hailoZustand(e: HailoErgebnis | undefined): { text: string; ton: Ton } | null {
  if (!e) return null;
  if (e.status === "fehler") return { text: "gescheitert", ton: "danger" };
  if (e.passt === true) return { text: "passt", ton: "ok" };
  if (e.passt === false) return { text: "passt nicht", ton: "warn" };
  return { text: e.status, ton: "leise" };
}

/** Was nachts ohne Auftrag umgewandelt wird (argus-hailo.timer, 04–08). */
export function hailoPlanText(s: Stand): string {
  if (s.hailo_auftrag) return `Lauf ${laufText(s.hailo_auftrag.lauf)} beauftragt (${s.hailo_auftrag.rechner}), forge nimmt ihn binnen 5 min`;
  if (s.hailo_nacht.length) return `${s.hailo_nacht.map(laufText).join(", ")} heute Nacht 04–08 Uhr`;
  return "";
}

/** Dauer je Phase in ihrer Reihenfolge: „parsen 7 s (homeserver2) · …". */
export function hailoDauer(phasen: Record<string, { dauer_s: number; rechner?: string }>): string {
  const reihe = ["wartet", "parsen", "optimieren", "kompilieren", "pruefen"];
  const rang = (k: string) => (reihe.includes(k) ? reihe.indexOf(k) : 99);
  const namen: Record<string, string> = { wartet: "gewartet", parsen: "parsen", optimieren: "optimieren", kompilieren: "kompilieren", pruefen: "prüfen" };
  return Object.entries(phasen)
    .sort((a, b) => rang(a[0]) - rang(b[0]))
    .map(([p, d]) => `${namen[p] ?? p} ${phasenDauer(d.dauer_s)}${d.rechner ? ` (${d.rechner})` : ""}`)
    .join(" · ");
}

// ── Schattenbetrieb (internal/argus/schatten.go) ─────────────────────

/** Zustand des Schattens in einem Wort und Ton. */
export function schattenZustand(sc: Schatten | undefined): { text: string; ton: Ton } {
  if (!sc) return { text: "unbekannt", ton: "leise" };
  if (!sc.eingerichtet) return { text: "nicht eingerichtet", ton: "warn" };
  if (sc.fehler && !sc.zustand) return { text: "Zentrale nicht erreichbar", ton: "warn" };
  if (!sc.verbunden || sc.ist_alt) return { text: "Detektor meldet sich nicht", ton: "warn" };
  switch (sc.zustand) {
    case "laeuft":
      return sc.pause ? { text: "pausiert", ton: "warn" } : { text: "läuft", ton: "ok" };
    case "laedt":
      return { text: "lädt", ton: "accent" };
    case "fehler":
      return { text: "Fehler", ton: "danger" };
    case "aus":
      return { text: "aus", ton: "leise" };
  }
  return { text: "unbekannt", ton: "leise" };
}

/** Schritt 5 der Kette. */
export function schattenHalt(sc: Schatten | undefined, hefDa: boolean): ArgusHalt {
  const basis = { key: "schatten", etikett: "Schattenbetrieb", wann: "ohne Wirkung", titel: "Das eigene Modell läuft im Detektor auf worker1 mit, ohne Wirkung" };
  // Läuft er, ist der Schritt erledigt wie die davor (ruhig, nicht „läuft
  // gerade“ wie ein Training): ort sagt es, wann nennt den Lauf.
  if (sc?.eingerichtet && sc.zustand === "laeuft" && sc.verbunden && !sc.ist_alt) {
    return { ...basis, ort: sc.pause ? "läuft, pausiert" : "läuft, ohne Wirkung", wann: sc.modell ? `Lauf ${laufText(sc.modell)}` : basis.wann, zustand: "da" };
  }
  if (sc?.zustand === "fehler") return { ...basis, ort: "Fehler", zustand: "fehler", titel: sc.grund ?? basis.titel };
  if (sc?.eingerichtet && sc.zustand === "aus") return { ...basis, ort: "aus", zustand: "pausiert" };
  return { ...basis, ort: hefDa ? "HEF bereit" : "noch nicht", zustand: "wartet" };
}

/** Ein Fenster (1h, 24h, 7d), sonst das erste. */
export function schattenFenster(sc: Schatten | undefined, name: string): SchattenFenster | undefined {
  return sc?.fenster.find((f) => f.name === name) ?? sc?.fenster[0];
}

/** Millisekunden, eine Nachkommastelle. */
export function ms(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return "—";
  return `${v.toLocaleString("de-DE", { maximumFractionDigits: 1 })} ms`;
}

/** Last des Produktivmodells mit gegen ohne Schatten, ein Satz. */
export function schattenLast(f: SchattenFenster | undefined): string {
  if (!f || !f.angeboten) return "noch keine Messung mit Schatten";
  const mit = `${ms(f.produktiv_ms)} mit Schatten (${zahl(f.angeboten)} Bilder)`;
  if (!f.produktiv_ohne_n) return mit;
  const d = f.produktiv_ms - f.produktiv_ohne_ms;
  const diff = Math.abs(d) < 0.05 ? "gleich" : `${d > 0 ? "+" : "−"}${Math.abs(d).toLocaleString("de-DE", { maximumFractionDigits: 1 })} ms`;
  return `${mit}, ${ms(f.produktiv_ohne_ms)} ohne (${zahl(f.produktiv_ohne_n)} Bilder) · ${diff}`;
}

/** Höhen der Score-Balken (0..1, größte Stufe = 1). */
export function scoreHoehen(score: readonly number[]): number[] {
  const m = Math.max(0, ...score);
  return score.map((v) => (m > 0 ? v / m : 0));
}

export const SCHATTEN_GRUND: Record<string, string> = {
  eigen: "eigene Klasse",
  treffer: "Treffer",
  abweichung: "Abweichung",
};

// ── Modelle: der Weg bis produktiv, Vergleich ────────────────────────

/** Die fünf Schritte eines Modells (Mockup argus-modul-2.html, „3 · Modelle“):
 *  Training → Hailo → worker1 → Schatten → produktiv. */
export function modellWeg(md: Modell | undefined, l: Lauf | undefined, sc: Schatten | undefined): ArgusHalt[] {
  const passt = md?.ergebnis?.passt ?? md?.hailo?.passt;
  const training: ArgusHalt = l?.aktiv
    ? { key: "training", etikett: "Training", ort: laufWas(l), wann: "läuft", zustand: "aktiv" }
    : { key: "training", etikett: "Training", ort: l?.map50 != null ? `mAP50 ${prozent(l.map50)}` : md ? "fertig" : "—", wann: l ? `${l.epochen} Epochen` : "", zustand: md || l?.phase === "fertig" ? "da" : l?.phase === "fehler" ? "fehler" : "wartet" };
  const hailo: ArgusHalt = l?.hailo_live?.aktiv
    ? { key: "hailo", etikett: "Hailo", ort: hailoWas(l.hailo_live), wann: "läuft", zustand: "aktiv" }
    : md?.hef
      ? { key: "hailo", etikett: "Hailo", ort: passt ? "passt" : passt === false ? "passt nicht" : "HEF da", wann: md.ergebnis?.map50_hef != null ? `HEF ${prozent(md.ergebnis.map50_hef)}` : "", zustand: passt === false ? "fehler" : "da" }
      : { key: "hailo", etikett: "Hailo", ort: "—", wann: "nach dem Training", zustand: "wartet" };
  const name = md?.lauf ?? l?.lauf ?? "";
  const beim = sc?.modelle.find((m) => m.name === name);
  const a = md?.ausrollen;
  const worker1: ArgusHalt =
    a?.phase === "bringen"
      ? { key: "worker1", etikett: "worker1", ort: "wird gebracht", wann: "", zustand: "aktiv" }
      : beim && !beim.fehler
        ? { key: "worker1", etikett: "worker1", ort: "liegt dort", wann: "", zustand: "da" }
        : a?.phase === "fehler"
          ? { key: "worker1", etikett: "worker1", ort: "Fehler", wann: "", zustand: "fehler", titel: a.fehler }
          : { key: "worker1", etikett: "worker1", ort: "—", wann: passt ? "bereit" : "", zustand: "wartet" };
  const imSchatten = sc?.modell === name && (sc.zustand === "laeuft" || sc.zustand === "laedt");
  const vorher = sc?.verlauf?.find((v) => v.modell === name && !(sc.modell === name));
  const schatten: ArgusHalt = imSchatten
    ? { key: "schatten", etikett: "Schatten", ort: sc?.zustand === "laedt" ? "lädt" : "läuft", wann: sc?.modell_seit ? `seit ${datum(sc.modell_seit)}` : "", zustand: "aktiv" }
    : vorher
      ? { key: "schatten", etikett: "Schatten", ort: "vorher", wann: `bis ${datum(vorher.bis)}`, zustand: "da" }
      : { key: "schatten", etikett: "Schatten", ort: "—", wann: "", zustand: "wartet" };
  const produktiv: ArgusHalt = { key: "produktiv", etikett: "Produktiv", ort: "Frigate+", wann: "", zustand: "wartet", titel: "Produktiv bleibt Frigate+, bis die Übernahme lohnt und Tim entscheidet" };
  return [training, hailo, worker1, schatten, produktiv];
}

/** mAP50 eines Satzes (nach Kästen gewichtet) als „77 %“, „—“ ohne Kästen. */
export function satzWert(v: VergleichSatz | undefined): string {
  if (!v || !v.kaesten) return "—";
  return prozent(v.map50_gewichtet ?? v.map50);
}

/** Bestes Modell je Satz (höchstes mAP50 gewichtet, mit Kästen). */
export function bestesJeSatz(modelle: Modell[], satz: "pruefsatz" | "frisch"): string | undefined {
  let best: { lauf: string; v: number } | undefined;
  for (const m of modelle) {
    const s = m.vergleich?.[satz];
    const v = s?.kaesten ? (s.map50_gewichtet ?? s.map50) : undefined;
    if (v != null && (!best || v > best.v)) best = { lauf: m.lauf, v };
  }
  return best?.lauf;
}

/** Ton einer Ampel für StatusBadge/Punkt. */
export function ampelTon(a: string): "ok" | "warn" | "danger" | "leise" {
  return a === "gruen" ? "ok" : a === "gelb" ? "warn" : a === "rot" ? "danger" : "leise";
}
